import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import NotifTemplateService from '../../../../services/notif-template.service';
import ProductService from '../../../../services/product.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';

import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface NotifTemplateRow {
  id: number;
  template_code: string;
  template_name: string;
  event_type: string;
  product_id: number | null;
  status: string;
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

const NotifTemplateList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'template_code' });
  const [items, setItems] = useState<NotifTemplateRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<NotifTemplateRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [products, setProducts] = useState<ProductOption[]>([]);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
  }, []);

  const { loading } = useFetch({
    service: NotifTemplateService,
    func: 'list',
    payload: { includeDeleted: showInactive, page, limit: perPage },
    dependency: [showInactive, page, perPage, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const productLabel = useCallback(
    (productId: number | null) => {
      if (!productId) return t('masters.notifTemplates.list.pageTexts.global');
      const product = products.find((p) => p.id === productId);
      return product ? product.product_code : productId;
    },
    [products, t]
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.notifTemplates}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await NotifTemplateService.remove(deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.notifTemplates.list.pageTexts.deleteSuccessSoft')
              : t('masters.notifTemplates.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'template_code', sortEnabled: true, width: '18%', label: t('masters.notifTemplates.list.pageTexts.table.columns.code') },
      { id: 'template_name', sortEnabled: true, width: '22%', label: t('masters.notifTemplates.list.pageTexts.table.columns.name') },
      { id: 'event_type', sortEnabled: true, width: '20%', label: t('masters.notifTemplates.list.pageTexts.table.columns.eventType') },
      { id: 'product_id', sortEnabled: true, width: '15%', label: t('masters.notifTemplates.list.pageTexts.table.columns.product') },
      { id: 'status', sortEnabled: true, width: '10%', label: t('masters.notifTemplates.list.pageTexts.table.columns.status') },
      { id: 'actions', sortEnabled: false, width: '15%', label: t('masters.notifTemplates.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: NotifTemplateRow; className?: string }) => (
      <tr className={className}>
        <td>{item.template_code}</td>
        <td>{item.template_name}</td>
        <td>{item.event_type}</td>
        <td>{productLabel(item.product_id)}</td>
        <td>
          <StatusBadge label={item.status} colour={item.status === 'active' ? '#2e7d32' : '#757575'} />
        </td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit, productLabel]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.notifTemplates.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.notifTemplates.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.notifTemplates.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.notifTemplates.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.notifTemplates}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.notifTemplates.list.pageTexts.deleteDialog.title')}
        content={t('masters.notifTemplates.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.notifTemplates.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.notifTemplates.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default NotifTemplateList;
